// SkinIcons.tsx
import * as React from 'react';

type IconProps = {
  className?: string;
  size?: number;          // fallback used only if className doesn't set w/h
  strokeWidth?: number;
  title?: string;
};

const base = ({ size = 20, className }: IconProps) =>
  className ?? `w-[${size}px] h-[${size}px]`;

export function IconEye(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Eye' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}

export function IconWrinkle(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Expression lines' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <path d="M3 8c3 2 6 2 9 0M3 16c3 2 6 2 9 0" />
      <path d="M12 8c3-2 6-2 9 0M12 16c3-2 6-2 9 0" opacity=".6" />
    </svg>
  );
}

export function IconTarget(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Warm tone saturation' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconTexture(props: IconProps) {
  const { strokeWidth = 1.6, title = 'Texture' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 12h16M12 4v16" />
      <path d="M8 4v16M4 8h16M16 4v16M4 16h16" opacity=".35" />
    </svg>
  );
}

export function IconAcne(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Texture flare-ups' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      {/* Simple serum bottle outline */}
      <path d="M10 3h4M11 3v3m2-3v3" />
      <rect x="7.5" y="6" width="9" height="13" rx="2" />
      {/* a couple of 'pore' dots */}
      <circle cx="10" cy="12" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="14" cy="15" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconVascular(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Deep tone saturation' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <path d="M5 20c3-6 3-10 7-10s4 4 7 10" />
      <path d="M9 12c1 0 2-1 3-3M15 17c1-1 2-2 3-2" opacity=".6" />
    </svg>
  );
}

export function IconDroplet(props: IconProps) {
  const { strokeWidth = 1.8, title = 'Hydration' } = props;
  return (
    <svg
      viewBox="0 0 24 24"
      className={base(props)}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={!title}
    >
      <title>{title}</title>
      <path d="M12 3c3 4 7 7 7 11.2A7 7 0 1 1 5 14.2C5 10 9 7 12 3Z" />
    </svg>
  );
}

/**
 * Map concern name -> icon component.
 * NOTE: matching is on the INTERNAL concern identifier (callers pass the stable
 * key such as `skin_vascular`/`darkcircle`); the `title` tooltips use the
 * abstracted, user-facing labels.
 */
export function iconForConcern(name: string): React.JSX.Element {
  const n = name.toLowerCase();
  if (n.includes('dark')) return <IconEye className="w-4 h-4" title="Under-eye area" />;
  if (n.includes('wrinkl')) return <IconWrinkle className="w-4 h-4" title="Expression lines" />;
  if (n.includes('red')) return <IconTarget className="w-4 h-4" title="Warm tone saturation" />;
  if (n.includes('texture')) return <IconTexture className="w-4 h-4" title="Texture" />;
  if (n.includes('acne')) return <IconAcne className="w-4 h-4" title="Texture flare-ups" />;
  if (n.includes('vasc')) return <IconVascular className="w-4 h-4" title="Deep tone saturation" />;
  return <IconDroplet className="w-4 h-4" title="Skin" />;
}
